import {
  Badge,
  Button,
  Flex,
  Progress,
  Select,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tabs,
  Text,
} from '@retracedgmbh/ui-components-lib';
import { useState } from 'react';
import useFeature from '@/hooks/useFeature';
import type { UploadFileData } from '@/hooks/useFileUploadNew';
import useTranslator from '@/hooks/useTranslator';
import type {
  ClassifyResultItem,
  FileClassificationType,
} from '@/services/api/types/ai-processing';
import BulkFileUpload from './BulkFileUpload';
import ItemCard from './ItemCard';
import type { AiProcessingItem, AiProcessingSummary } from './types';

interface AIProcessingSheetProps {
  isOpen: boolean;
  onClose: () => void;
  data: AiProcessingSummary | undefined;
  isLoading: boolean;
  filter: string;
  onFilterChange: (filter: string) => void;
  onCta: (item: AiProcessingItem) => void;
  bulkUploadBatchKey: number;
  bulkUploadFiles: UploadFileData[];
  onBulkUploadFinish: (files: UploadFileData[]) => void;
  isBulkClassifying: boolean;
  classifyResults?: ClassifyResultItem[];
  typeOverrides: Record<string, FileClassificationType>;
  onTypeChange: (fileId: string, type: FileClassificationType) => void;
  onAccept: () => Promise<boolean>;
  isAccepting: boolean;
  onCancel: () => void;
  isPolling: boolean;
}

const TRANSLATION_PAGE = 'ai-processing';

export default function AIProcessingSheet({
  isOpen,
  onClose,
  data,
  isLoading,
  filter,
  onFilterChange,
  onCta,
  bulkUploadBatchKey,
  bulkUploadFiles,
  onBulkUploadFinish,
  isBulkClassifying,
  classifyResults,
  typeOverrides,
  onTypeChange,
  onAccept,
  isAccepting,
  onCancel,
  isPolling,
}: AIProcessingSheetProps) {
  const { isDropBoxEnabled } = useFeature();
  const { getTranslation } = useTranslator({ page: TRANSLATION_PAGE });
  const [activeTab, setActiveTab] = useState('queue');

  const FILTER_OPTIONS = [
    { key: 'ALL', text: getTranslation('filter.all') },
    { key: 'COMPLETED', text: getTranslation('filter.completedFirst') },
    { key: 'PROCESSING', text: getTranslation('filter.processingFirst') },
    { key: 'FAILED', text: getTranslation('filter.failedFirst') },
  ];
  const stats = data?.stats;
  const items = data?.items ?? [];

  const hasFiles = bulkUploadFiles.length > 0;
  const allClassified = classifyResults?.length
    ? classifyResults.every(
        r => r.status === 'completed' || r.status === 'failed',
      )
    : false;
  const classifiedCount =
    classifyResults?.filter(r => r.status === 'completed').length ?? 0;

  const handleStartProcessing = async () => {
    const success = await onAccept();
    if (success) {
      setActiveTab('queue');
    }
  };

  const handleCancel = () => {
    onCancel();
    onClose();
  };

  const tabItems = [
    {
      key: 'queue',
      label: (
        <Flex gap='sm' alignItems='center'>
          <i
            className={`${activeTab === 'queue' ? 'fa-solid' : 'fa-regular'} fa-sparkles`}
          />
          {getTranslation('tabs.queue')}
        </Flex>
      ),
      children: (
        <>
          {/* Processing files summary */}
          <Flex direction='column' gap='xs' paddingY='md' margin='2xl'>
            <Flex alignItems='center' gap='md'>
              <Flex
                alignItems='center'
                justify='center'
                backgroundColor='secondary'
                borderRadius='md'
                className='tw:w-8 tw:h-8'
              >
                <i className='fa-regular fa-file tw:text-content-secondary tw:text-xs' />
              </Flex>
              <Flex direction='column' gap='xs' className='tw:flex-1'>
                <Flex alignItems='center' gap='sm'>
                  <Text size='base' weight='semibold'>
                    {getTranslation('queue.processingFiles')}
                  </Text>
                  {stats ? (
                    <Flex gap='xs'>
                      <Badge type='primary' radius='squared'>
                        {getTranslation('queue.processing', {
                          count: stats.processing,
                        })}
                      </Badge>
                      {stats.failed > 0 && (
                        <Badge type='destructive' radius='squared'>
                          {getTranslation('queue.error', {
                            count: stats.failed,
                          })}
                        </Badge>
                      )}
                      <Badge type='secondary' radius='squared'>
                        {getTranslation('queue.completed', {
                          count: stats.completed,
                        })}
                      </Badge>
                    </Flex>
                  ) : null}
                </Flex>
                {stats ? (
                  <Flex alignItems='center' gap='sm'>
                    <Flex className='tw:flex-1'>
                      <Progress className='tw:h-2' value={stats.percentage} />
                    </Flex>
                    <Text
                      size='small'
                      weight='semibold'
                      color='secondary'
                      className='tw:whitespace-nowrap'
                    >
                      {getTranslation('queue.percentComplete', {
                        percentage: stats.percentage,
                      })}
                    </Text>
                  </Flex>
                ) : null}
              </Flex>
            </Flex>
          </Flex>

          <Flex direction='column' gap='md' padding='2xl'>
            {/* Task queue section */}
            <Flex direction='column' gap='md'>
              <Flex justify='between' alignItems='center'>
                <Flex direction='column' gap='xs' className='tw:flex-1'>
                  <Text size='base' weight='semibold'>
                    {getTranslation('queue.taskQueueTitle')}
                  </Text>
                  <Text size='small' color='secondary'>
                    {getTranslation('queue.itemsInQueue', {
                      count: items.length,
                    })}
                  </Text>
                </Flex>
                <Flex alignItems='center' gap='sm'>
                  <Text size='small' weight='semibold'>
                    {getTranslation('queue.showLabel')}
                  </Text>
                  <Select
                    options={FILTER_OPTIONS}
                    value={FILTER_OPTIONS.find(o => o.key === filter) ?? null}
                    onChange={(opt: { key: string }) => onFilterChange(opt.key)}
                    width={160}
                  />
                </Flex>
              </Flex>

              {isLoading ? (
                <Flex justify='center' padding='lg'>
                  <i className='fa-light fa-circle-notch tw:animate-spin tw:text-content-secondary tw:text-2xl' />
                </Flex>
              ) : items.length === 0 ? (
                <Flex
                  direction='column'
                  alignItems='center'
                  padding='xl'
                  gap='sm'
                >
                  <i className='fa-regular fa-inbox tw:text-content-secondary tw:text-2xl' />
                  <Text color='secondary'>
                    {getTranslation('queue.noItems')}
                  </Text>
                </Flex>
              ) : (
                <Flex direction='column' gap='sm'>
                  {items.map((item, index) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      index={index + 1}
                      onCta={onCta}
                    />
                  ))}
                </Flex>
              )}
            </Flex>
          </Flex>
        </>
      ),
    },
    ...(isDropBoxEnabled
      ? [
          {
            key: 'upload',
            label: (
              <Flex gap='sm' alignItems='center'>
                <i
                  className={`${activeTab === 'upload' ? 'fa-solid' : 'fa-regular'} fa-cloud-arrow-up`}
                />
                {getTranslation('tabs.upload')}
                {classifiedCount > 0 && (
                  <span className='tw:flex tw:items-center tw:justify-center tw:min-w-5 tw:h-5 tw:rounded-full tw:bg-content-info-ai-bold tw:px-1'>
                    <Text
                      as='span'
                      className='tw:text-xs tw:leading-none tw:text-white tw:font-semibold'
                    >
                      {classifiedCount}
                    </Text>
                  </span>
                )}
              </Flex>
            ),
            children: (
              <Flex direction='column' padding='2xl'>
                <BulkFileUpload
                  batchKey={bulkUploadBatchKey}
                  uploadedFiles={bulkUploadFiles}
                  onUploadFinish={onBulkUploadFinish}
                  isDisabled={isBulkClassifying}
                  classifyResults={classifyResults}
                  typeOverrides={typeOverrides}
                  onTypeChange={onTypeChange}
                />
              </Flex>
            ),
          },
        ]
      : []),
  ];

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent className='tw:w-[568px] tw:max-w-[568px] tw:p-0 tw:flex tw:flex-col tw:overflow-hidden'>
        <SheetHeader className='tw:p-6 tw:pb-0 tw:shrink-0'>
          <Flex direction='column' gap='xs'>
            <SheetTitle>{getTranslation('sheet.title')}</SheetTitle>
            <Text size='small' color='secondary'>
              {getTranslation('sheet.description')}
            </Text>
          </Flex>
        </SheetHeader>

        <Flex
          direction='column'
          className='tw:flex-1 tw:overflow-y-auto tw:min-h-0'
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            className='tw:px-6'
          />
        </Flex>

        {activeTab === 'upload' && hasFiles && (
          <Flex
            justify='end'
            alignItems='center'
            gap='md'
            padding='2xl'
            className='tw:border-t tw:border-border-primary tw:shrink-0'
          >
            <Button variant='ghost' onClick={handleCancel}>
              {getTranslation('upload.cancel')}
            </Button>
            <Button
              variant='primary'
              onClick={handleStartProcessing}
              disabled={!allClassified || isAccepting || isPolling}
              loading={isAccepting}
              icon='fa-regular fa-arrow-right'
              iconPosition='right'
            >
              {getTranslation('upload.startProcessing')}
            </Button>
          </Flex>
        )}
      </SheetContent>
    </Sheet>
  );
}
